import { MouseEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, SocialIcon, SocialIconProps, SidebarMenuItem, Icon } from '@anytag/shared-atoms';
import { MenuTitle, MenuType } from '@anytag/router';
import { cn } from '@anytag/shared-utils';
import { getMenuIcon } from './utils';

interface MenuItemProps extends MenuType {
  size?: number;
  target?: string;
  hasIcon?: boolean;
  keyPrefix?: string;
  className?: string;
  rightNode?: ReactNode;
  activeIcon?: boolean;
  activeTitle?: MenuTitle;
  disabled?: boolean | null;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export const MenuItem = ({
  to,
  href,
  params,
  title,
  rightNode,
  className,
  groupTitle,
  activeIcon,
  activeTitle,
  hasIcon = true,
  target,
  keyPrefix = 'Routes',
  onClick,
  disabled,
  size = 20
}: MenuItemProps) => {
  const { t } = useTranslation();

  const menuTitle = groupTitle || title;
  const socialIcon: SocialIconProps['type'] | undefined = (() => {
    switch (menuTitle) {
      case MenuTitle.TikTok:
        return 'TIKTOK';
      case MenuTitle.Instagram:
        return 'INSTAGRAM';
      default:
        return undefined;
    }
  })();

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!(href || to)) {
      e.preventDefault();
    }

    onClick?.(e);
  };

  return (
    <SidebarMenuItem>
      <Anchor
        to={to}
        href={href}
        params={params}
        target={target}
        onClick={handleClick}
        className={cn(
          'group/menu-item mb-1 flex h-8 items-center rounded bg-transparent px-2 text-secondary transition-opacity duration-300',
          'hover:text-primary hover:no-underline',
          className,
          disabled && 'pointer-events-none opacity-20',
          activeTitle === title && !disabled && 'bg-white text-primary shadow-container'
        )}
        activeOptions={{ includeSearch: false }}
      >
        {hasIcon ? (
          <div className="flex w-5 items-center">
            {socialIcon ? (
              <SocialIcon size={size} type={socialIcon} variant={activeIcon && !disabled ? 'original' : 'gray'} />
            ) : (
              <Icon
                icon={getMenuIcon(menuTitle)}
                variant={activeIcon && !disabled ? 'black' : 'menu-default'}
                width={size}
                height={size}
                className="group-hover/menu-item:icon-color-primary"
              />
            )}
          </div>
        ) : null}
        <p
          className={cn(
            'menu-item-title ml-2 flex-1 truncate text-md leading-4 font-semibold text-inherit',
            '[&:lang(ja)]:text-sm [&:lang(ja)]:leading-3'
          )}
        >
          {t(`${keyPrefix ? `${keyPrefix}.` : ''}${menuTitle}`)}
        </p>
        {rightNode}
      </Anchor>
    </SidebarMenuItem>
  );
};
